/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/CustomerCompanyUserModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import styles from "./index.module.css";
import { selectStyles, getSelectedCountryCodeOption } from "../../utils/common";
import CountryCodeSelector from "../../components/CountryCodeSelector";
import useCustomerCompanyUserModal from "../../hooks/useCustomerCompanyUserModal";

import { ReactComponent as PersonIcon } from "../../assets/icons/CustomerCompanyUserModal/PersonIcon.svg";
import { ReactComponent as EmailIcon } from "../../assets/icons/CustomerCompanyUserModal/EmailIcon.svg";

const CustomerCompanyUserModal = ({
  open,
  onClose,
  companyId,
  user,
  mode = "create",
  onUserCreated,
}) => {
  const {
    t,
    countryCodeOptions,
    dialogRef,
    name,
    email,
    phoneCountryCode,
    phone,
    position,
    loading,
    enabled,
    emailError,
    emailFormatInvalid,
    isEditMode,
    isFormValid,
    handleSubmit,
    handleNameChange,
    handleEmailChange,
    handlePositionChange,
    handleCountryCodeChange,
    handlePhoneChange,
    handleEnabledChange,
    getEmailErrorMessage,
    getModalTitle,
    getSubmitButtonText,
  } = useCustomerCompanyUserModal({
    open,
    onClose,
    companyId,
    user,
    mode,
    onUserCreated,
  });

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.modalContent}>
          <label className={styles.modalTitle}>{getModalTitle()}</label>
          <form onSubmit={handleSubmit}>
            <div className={styles.formContainer}>
              <div className={styles.formRow}>
                <div className={styles.formCol}>
                  <label className={styles.label}>
                    {t("components.customerCompanyUserModal.nameLabel")}
                  </label>
                  <div className={styles.inputWrapper}>
                    <PersonIcon className={styles.inputIcon} />
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={handleNameChange}
                      className={`${styles.input} ${styles.inputWithIcon}`}
                      placeholder={t(
                        "components.customerCompanyUserModal.namePlaceholder",
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formCol}>
                  <label className={styles.label}>
                    {t("components.customerCompanyUserModal.emailLabel")}
                  </label>
                  <div className={styles.inputWrapper}>
                    <EmailIcon className={styles.inputIcon} />
                    <input
                      type="email"
                      name="email"
                      value={email}
                      disabled={isEditMode}
                      onChange={handleEmailChange}
                      className={`${styles.input} ${styles.inputWithIcon}`}
                      placeholder={t(
                        "components.customerCompanyUserModal.emailPlaceholder",
                      )}
                      style={{
                        borderColor: emailError || emailFormatInvalid ? "red" : "",
                      }}
                    />
                  </div>
                  {(emailError || emailFormatInvalid) && (
                    <div className={styles.error}>{getEmailErrorMessage()}</div>
                  )}
                </div>
                <div className={styles.formCol}>
                  <label className={styles.label}>
                    {t("components.customerCompanyUserModal.positionLabel")}
                  </label>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      name="position"
                      value={position}
                      onChange={handlePositionChange}
                      className={styles.input}
                      placeholder={t(
                        "components.customerCompanyUserModal.positionPlaceholder",
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formCol}>
                  <label className={styles.label}>
                    {t("components.customerCompanyUserModal.officePhoneLabel")}
                  </label>
                  <div className={styles.phoneInputContainer}>
                    <CountryCodeSelector
                      value={getSelectedCountryCodeOption(
                        countryCodeOptions,
                        phoneCountryCode,
                      )}
                      onChange={handleCountryCodeChange}
                      styles={selectStyles}
                    />
                    <input
                      type="text"
                      className={styles.input}
                      placeholder={t(
                        "components.customerCompanyUserModal.officePhonePlaceholder",
                      )}
                      value={phone}
                      onChange={handlePhoneChange}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.switchRow}>
              <label className={styles.switchLabel}>
                {t("components.customerCompanyUserModal.enableDisableUser")}
              </label>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={handleEnabledChange}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
            <div className={styles.modalButtons}>
              <button
                type="button"
                onClick={onClose}
                className={styles.cancelButton}
                disabled={loading}
              >
                {t("components.customerCompanyUserModal.cancelButton")}
              </button>
              <button
                type="submit"
                className={styles.createButton}
                disabled={loading || !isFormValid()}
              >
                {getSubmitButtonText()}
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </>
  );
};

export default CustomerCompanyUserModal;
